import { Component, Inject, OnInit } from '@angular/core';

import { translate } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { Constants } from 'app/constants/constants';
import { SemantriaAccountService } from 'app/providers/semantria-api/semantria-account.service';
import { Field, FieldService, FieldTypeOption } from 'app/providers/storage-api/field.service';

import { MatDialogRef, MAT_DIALOG_DATA } from 'app/services/dialog.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { ErrorObjectUtils } from 'app/utils/error-object.utils';

export interface MetadataField {
  originalFieldName: string;
  fieldName: string;
  fieldType: string;
  typeDetail: string;
  filterable: boolean;
}

export interface FieldTypeDialogOptions {
  fieldName: string;
  fieldType: string;
  filterable: boolean;
  allFields: Field[];
}

export const FIELD_TYPES = [
  {
    value: 'keyword',
    label: 'Keyword',
    detail:
      'Keywords are used for categorization. They can be things like Region or Make or Model, etc. They are not used for comments or verbatims.',
  },
  {
    value: 'rating',
    label: 'Rating',
    detail: 'Ratings are answers to questions on such as a 1-5 or 1-10 scale.',
  },
  {
    value: 'long',
    label: 'Integer',
    detail:
      'Integers are numbers that are not ratings. They can be used for things like number of purchases, number of posts, etc.',
  },
  {
    value: 'double',
    label: 'Float',
    detail: 'Floating Point Numbers are used for things such as currency amounts.',
  },
  {
    value: 'weight',
    label: 'Weight',
    detail: 'Weights are applied when calculating metrics such as impact.',
  },
  { value: 'date', label: 'Date', detail: 'Dates can be used for filtering.' },
];

@EnableClassTelemetry()
@Component({
  selector: 'app-field-type-dialog',
  templateUrl: './field-type-dialog.component.html',
  styleUrls: ['./field-type-dialog.component.scss'],
  standalone: false,
})
export class FieldTypeDialogComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  public isLoading = { configId: false };

  public details = '';
  public metadataField: MetadataField;
  public fieldNameStatus: ErrorObject = { state: EnumErrorStates.SUCCESS, text: '' };
  existingFieldNames: string[] = [];
  accountDetailsSub$: Subscription;
  public labelMaxLength = Constants.SPOTLIGHT_MAX_SHORT_FIELD_DEFAULT_LENGTH;
  public fieldTypeOptions: FieldTypeOption[];

  constructor(
    private accountService: SemantriaAccountService,
    private dialogRef: MatDialogRef<FieldTypeDialogComponent>,
    private fieldService: FieldService,
    @Inject(MAT_DIALOG_DATA) private options: FieldTypeDialogOptions
  ) {}

  ngOnInit() {
    this.accountDetailsSub$ = this.accountService.sub.subscribe((accountDetails) => {
      this.labelMaxLength =
        accountDetails?.limits?.SPOTLIGHT_MAX_SHORT_FIELD_LENGTH ??
        Constants.SPOTLIGHT_MAX_SHORT_FIELD_DEFAULT_LENGTH;
    });
    this.fieldTypeOptions = this.fieldService.getFieldTypeOptions();
    this.refresh();
  }

  public onValidateFieldName(mdField) {
    this.metadataFieldNameValidator(
      mdField.fieldName,
      this.fieldNameStatus,
      translate('field-type-dialog.metadata-field-name'),
      this.labelMaxLength,
      true
    );
  }

  metadataFieldNameValidator(
    toValidate: string,
    errorObject: ErrorObject,
    errorTitle: string,
    maxLength: number,
    required: boolean
  ): void {
    if (required && (!toValidate || !toValidate.trim())) {
      ErrorObjectUtils.setError(
        errorObject,
        translate('field-type-dialog.valid-metadata-field-name')
      );
      return;
    }

    if (!FieldService.FIELD_VALID_RE.test(toValidate)) {
      ErrorObjectUtils.setError(errorObject, translate('field-type-dialog.invalid-name'));
      return;
    }

    if (toValidate.length === maxLength) {
      ErrorObjectUtils.setWarning(
        errorObject,
        translate('field-type-dialog.maximum length-is', { title: errorTitle, maxLength })
      );
      return;
    }

    if (toValidate.length > maxLength) {
      ErrorObjectUtils.setError(
        errorObject,
        translate('field-type-dialog.maximum length-is', { title: errorTitle, maxLength })
      );
      return;
    }

    const normalizedName = toValidate.toLowerCase().replace(FieldService.NORMALIZED_NAME_RE, '');
    if (this.options.allFields.some((field) => normalizedName === field.normalizedName)) {
      ErrorObjectUtils.setError(
        errorObject,
        translate('field-type-dialog.metadata-field-with-similar-name')
      );
      return;
    }

    ErrorObjectUtils.setNoError(errorObject);
  }

  setOptionDetail(mdField, option) {
    mdField.typeDetail = option.detail;
  }

  public get fieldName(): string {
    return this.options.fieldName;
  }

  public async refresh(): Promise<void> {
    let fieldName = this.options.fieldName;
    if (this.options.fieldName.length > this.labelMaxLength) {
      fieldName = this.options.fieldName.substring(0, this.labelMaxLength);
    }
    this.metadataField = {
      originalFieldName: this.options.fieldName,
      fieldName,
      fieldType: this.options.fieldType,
      typeDetail: '',
      filterable: this.options.filterable,
    };
    this.onValidateFieldName(this.metadataField);
  }

  public close(): void {
    this.dialogRef.close();
  }

  public isComplete(): boolean {
    return (
      this.fieldNameStatus.state !== EnumErrorStates.ERROR && this.metadataField.fieldType != null
    );
  }

  public async confirm(): Promise<void> {
    const result = {
      metadataField: this.metadataField,
    };

    // FIXME: SNACKBAR?
    this.dialogRef.close(result);
  }
}
